import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminDemoData } from '@platform/client';
import { Button, InlineFormInput } from '@platform/components';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type MatchType = 'individual' | 'team';

type IndividualMatchFormData = {
  professionalId: number;
  projectId: number;
};

type TeamMatchFormData = {
  projectId: number;
  cohortId?: number;
};

export const CreateDemoMatchModal = (props: { onClose: () => void }) => {
  const [matchType, setMatchType] = useState<MatchType>('individual');
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const [successText, setSuccessText] = useState<string>();

  const individualForm = useForm<IndividualMatchFormData>({ mode: 'onChange' });
  const teamForm = useForm<TeamMatchFormData>({ mode: 'onChange' });

  const createIndividualMatchMutator = AdminDemoData.useCreateDemoIndividualMatch({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: (data) => {
        setSuccessText(
          `Professional #${data.professionalId} matched to project #${data.projectId}.`,
        );
        setModalStage('success');
      },
    },
  });

  const createTeamMatchMutator = AdminDemoData.useCreateDemoTeamMatch({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: (data) => {
        setSuccessText(
          `Team #${data.teamId} (members ${data.memberIds.join(', ')}, leader #${data.teamLeaderId}, cohort #${data.cohortId}) matched to project #${data.projectId}.`,
        );
        setModalStage('success');
      },
    },
  });

  const onSubmitIndividual: SubmitHandler<IndividualMatchFormData> = (data) => {
    createIndividualMatchMutator.mutate({
      data: { professionalId: data.professionalId, projectId: data.projectId },
    });
  };

  const onSubmitTeam: SubmitHandler<TeamMatchFormData> = (data) => {
    createTeamMatchMutator.mutate({
      data: { projectId: data.projectId, cohortId: data.cohortId || null },
    });
  };

  return (
    <AdminFormModal
      title='Create Demo Match'
      onClose={props.onClose}
      modalStage={modalStage}
      successText={successText}
      submitCTA={{
        text: 'Create match',
        callback: () => {
          if (matchType === 'individual') {
            individualForm.handleSubmit(onSubmitIndividual)();
          } else {
            teamForm.handleSubmit(onSubmitTeam)();
          }
        },
      }}
    >
      <div className='space-y-3'>
        <div className='flex gap-2'>
          <Button
            variant={matchType === 'individual' ? 'primary' : 'secondary-color'}
            size='sm'
            onPress={() => setMatchType('individual')}
          >
            Individual
          </Button>
          <Button
            variant={matchType === 'team' ? 'primary' : 'secondary-color'}
            size='sm'
            onPress={() => setMatchType('team')}
          >
            Team
          </Button>
        </div>
        {matchType === 'individual' ?
          <div className='space-y-2'>
            <InlineFormInput
              register={() =>
                individualForm.register('professionalId', {
                  required: 'Professional ID is required',
                })
              }
              label='Professional ID'
              placeholder='Enter pro user id'
              errors={individualForm.formState.errors.professionalId}
            />
            <InlineFormInput
              register={() =>
                individualForm.register('projectId', { required: 'Project ID is required' })
              }
              label='Project ID'
              placeholder='Enter project id'
              errors={individualForm.formState.errors.projectId}
            />
          </div>
        : <div className='space-y-2'>
            <p className='text-sm text-gray-600'>
              Creates a few fresh, already-approved demo professionals and a team made up of
              them, then matches the team to the project below — team members are always created
              fresh here rather than picked from existing users, so there's no risk of a member
              ending up on a team outside their actual cohort.
            </p>
            <InlineFormInput
              register={() =>
                teamForm.register('projectId', { required: 'Project ID is required' })
              }
              label='Project ID'
              placeholder='Enter project id'
              errors={teamForm.formState.errors.projectId}
            />
            <InlineFormInput
              register={() => teamForm.register('cohortId')}
              label='Cohort ID (optional)'
              placeholder='Leave blank for evergreen cohort'
              errors={undefined}
            />
          </div>
        }
      </div>
    </AdminFormModal>
  );
};
